/**
 * rother3 companion — HTML escaping
 *
 * ## Why this file exists
 *
 * `compare.js` and `workspace.js` each had their own `escapeHtml`, and both were written as
 *
 * ```js
 * const div = document.createElement('div');
 * div.textContent = text;
 * return div.innerHTML;
 * ```
 *
 * which is correct in a browser and **throws `ReferenceError: document is not defined`**
 * anywhere else. That single line is why `compare.js` had no tests at all: it could not be
 * imported into the test environment to be tested. A module nobody can load is a module
 * nobody has checked.
 *
 * It also meant every escaping site had to be re-verified by reading it, because none of
 * them could be exercised.
 *
 * ## Why the string version, not a DOM one
 *
 * The DOM version is arguably more correct for a lone `&` or `<`, and the string version
 * handles both. The string version also needs no DOM, so it is testable, deterministic, and
 * has no behaviour that differs between a browser and a test runner — which is the property
 * that was missing.
 *
 * Two escaping forms are needed, and they are not interchangeable:
 *
 * - `escapeHtml` for **text** going into element content or a quoted attribute.
 * - `escapeAttribute` for a value going inside an attribute, which additionally has to
 *   escape a quote so it cannot break out of the attribute.
 *
 * Getting that second case wrong is how `"` inside a business name escapes an `aria-label`
 * and injects an attribute.
 */

const TEXT_ENTITIES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
};

/**
 * Escape text for insertion into HTML content or a double-quoted attribute.
 *
 * @param {unknown} text - anything; non-strings are stringified first
 * @returns {string} escaped text
 */
export function escapeHtml(text) {
  return String(text ?? '').replace(/[&<>]/g, (c) => TEXT_ENTITIES[c]);
}

/**
 * Escape a value for an attribute, including the quote that would close it.
 *
 * The difference from `escapeHtml` is the double quote. Without it,
 * `title="He said "hi""` breaks out of the attribute, and whatever follows is parsed as
 * markup — which is how a business name from an imported file becomes script.
 *
 * @param {unknown} text
 * @returns {string}
 */
export function escapeAttribute(text) {
  return String(text ?? '')
    .replace(/[&<>"]/g, (c) => (c === '"' ? '&quot;' : TEXT_ENTITIES[c]))
    // A single quote matters in a single-quoted attribute.
    .replace(/'/g, '&#39;');
}
